import React, { useEffect, useState } from 'react';
import client from '../../../api/client';
import Toggle from '../../../components/Toggle';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function EmailTags({ emails, onChange }) {
  const [input, setInput] = useState('');

  const add = () => {
    const v = input.trim();
    if (v && !emails.includes(v)) onChange([...emails, v]);
    setInput('');
  };

  const remove = (e) => onChange(emails.filter(x => x !== e));

  return (
    <div className="flex flex-wrap gap-1.5 p-2 border border-brand-200 rounded-xl min-h-10 bg-white cursor-text">
      {emails.map(e => (
        <span key={e} className="inline-flex items-center gap-1 bg-brand-100 text-brand-700 text-xs font-medium px-2 py-0.5 rounded-full">
          {e}
          <button onClick={() => remove(e)} className="text-brand-400 hover:text-brand-700 leading-none">×</button>
        </span>
      ))}
      <input
        type="email"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => (e.key === 'Enter' || e.key === ',') && (e.preventDefault(), add())}
        onBlur={add}
        placeholder="Add email, press Enter"
        className="flex-1 min-w-32 outline-none text-xs text-gray-700 bg-transparent px-1"
      />
    </div>
  );
}

function Section({ title, desc, enabled, onToggle, children }) {
  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-bold text-brand-500 text-sm">{title}</h2>
          <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
        </div>
        <Toggle enabled={enabled} onToggle={onToggle} />
      </div>
      <div className={`space-y-4 ${!enabled ? 'opacity-40 pointer-events-none' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export default function NotificationSettings() {
  const [cfg, setCfg] = useState({
    breach_alert_enabled: true,
    breach_alert_emails: [],
    open_ticket_alert_enabled: true,
    open_ticket_threshold_hours: 48,
    open_ticket_emails: [],
    weekly_summary_enabled: false,
    weekly_summary_emails: [],
    weekly_summary_day: 0,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    client.get('/config/notifications/').then(r => setCfg(r.data)).catch(() => {});
  }, []);

  const update = (key, val) => setCfg(c => ({ ...c, [key]: val }));

  const save = async () => {
    setSaving(true);
    await client.patch('/config/notifications/', cfg);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      <Section
        title="SLA Breach Alerts"
        desc="Send an email when a location's uptime drops below the SLA target"
        enabled={cfg.breach_alert_enabled}
        onToggle={() => update('breach_alert_enabled', !cfg.breach_alert_enabled)}
      >
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Recipients</label>
          <EmailTags emails={cfg.breach_alert_emails} onChange={v => update('breach_alert_emails', v)} />
        </div>
      </Section>

      <Section
        title="Open Ticket Alerts"
        desc="Send an alert when a ticket has been open longer than the threshold"
        enabled={cfg.open_ticket_alert_enabled}
        onToggle={() => update('open_ticket_alert_enabled', !cfg.open_ticket_alert_enabled)}
      >
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Alert after (hours)</label>
          <input
            type="number"
            min={1}
            value={cfg.open_ticket_threshold_hours}
            onChange={e => update('open_ticket_threshold_hours', +e.target.value)}
            className="input-field w-28"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Recipients</label>
          <EmailTags emails={cfg.open_ticket_emails} onChange={v => update('open_ticket_emails', v)} />
        </div>
      </Section>

      <Section
        title="Weekly SLA Summary"
        desc="Send a weekly summary report of SLA performance across all locations"
        enabled={cfg.weekly_summary_enabled}
        onToggle={() => update('weekly_summary_enabled', !cfg.weekly_summary_enabled)}
      >
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Send every</label>
          <select
            value={cfg.weekly_summary_day}
            onChange={e => update('weekly_summary_day', +e.target.value)}
            className="input-field w-40"
          >
            {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Recipients</label>
          <EmailTags emails={cfg.weekly_summary_emails} onChange={v => update('weekly_summary_emails', v)} />
        </div>
      </Section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-xl hover:bg-brand-600 transition-colors disabled:opacity-50 shadow-sm"
        >
          {saved ? '✓ All Settings Saved' : saving ? 'Saving…' : 'Save All Notification Settings'}
        </button>
      </div>
    </div>
  );
}
